import React, { useMemo, useState } from 'react';

import { getCrmRecordCopy } from '@aline/common';
import type { CrmRecordCopy } from '@aline/common';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Input,
  InputAdornment,
  Typography,
} from '@mui/material';
import type {
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
} from '@mui/x-data-grid-premium';
import { DataGridPremium, gridClasses, useGridApiRef } from '@mui/x-data-grid-premium';
import { AlineButton, AlineCheckbox, SmallCloseIconButton, Spacer } from 'src/components';
import { GridAlineTooltip } from 'src/components/generic/data-grid/grid-aline-tooltip';
import { DoubleCheckIcon } from 'src/components/icons/DoubleCheckIcon';
import { IconColors } from 'src/components/icons/icon-utils';
import { SearchIcon } from 'src/components/icons/SearchIcon';
import type { Apollo } from 'src/gql/generated';
import { useDataGridSearch, useGetSelectedRowIds } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { useGetOpportunitiesWithPagination } from './hooks';

const useLinkWorkflowToOpportunityStyles = makeStyles()(theme => ({
  closeButton: {
    alignSelf: 'flex-start',
    marginLeft: 'auto',
  },
  dialogActions: {
    borderTop: `1px solid ${theme.appColors.rule}`,
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    alignItems: 'baseline',
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(2),
  },
  paper: {
    borderRadius: theme.dialogStyles.borderRadius,
    width: theme.dialogStyles.mediumDialogWidth,
  },
}));

export function LinkWorkflowToOpportunityDialog({
  integration,
  onClose,
  onSubmit,
  open,
}: {
  integration: Apollo.Integration;
  onClose: () => void;
  onSubmit: (opportunityId: string) => Promise<void>;
  open: boolean;
}) {
  const { classes } = useLinkWorkflowToOpportunityStyles();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<string | null>(null);
  const { article, lower } = getCrmRecordCopy(integration);

  return (
    <Dialog
      classes={{ paper: classes.paper }}
      open={open}
      slotProps={{
        transition: { onExit: () => setSelectedOpportunityId(null) },
      }}
      onClose={onClose}
    >
      <DialogContent className={classes.dialogContent}>
        <header className={classes.header}>
          <Typography variant="h1">{`Link to ${article} ${lower}`}</Typography>
          <SmallCloseIconButton className={classes.closeButton} onClick={onClose} />
        </header>
        <LinkDocumentDialogBody
          integration={integration}
          onSelectChange={setSelectedOpportunityId}
        />
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <AlineButton color="primary" variant="outlined" onClick={onClose}>
          Cancel
        </AlineButton>
        <AlineButton
          color="primary"
          disabled={!selectedOpportunityId}
          loading={submitting}
          variant="contained"
          onClick={async () => {
            if (!selectedOpportunityId) return;

            setSubmitting(true);
            await onSubmit(selectedOpportunityId);
            onClose();
            setSubmitting(false);
          }}
        >
          Link
        </AlineButton>
      </DialogActions>
    </Dialog>
  );
}

const useLinkDocumentDialogStyles = makeStyles()(theme => ({
  header: {
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(3),
  },
  main: {
    display: 'flex',
    height: 320,
  },
  search: {
    backgroundColor: theme.appColors.background,
    padding: theme.spacing(2),
  },
}));

type OpportunityRow = Pick<Apollo.ApiDeckOpportunity, 'id' | '__typename' | 'title' | 'type'>;

export function LinkDocumentDialogBody({
  integration,
  onSelectChange,
}: {
  integration: Apollo.Integration;
  onSelectChange: (selectedOpportunityId: string | null) => void;
}) {
  const apiRef = useGridApiRef();
  const { classes } = useLinkDocumentDialogStyles();
  const { fetchMoreOpportunities, loading, opportunities } = useGetOpportunitiesWithPagination({
    apiRef,
    integration,
  });
  const { article, label, lower, plural } = getCrmRecordCopy(integration);
  const colDefs = useMemo((): GridColDef<OpportunityRow>[] => {
    return [
      {
        field: 'title',
        flex: 1,
        headerName: `${label} Name`,
        renderCell: OpportunityTitle,
        renderHeader: OpportunityHeader,
        sortable: false,
        type: 'string',
      },
    ];
  }, [label]);
  // Slot components must keep a stable identity or the grid remounts them (and drops search state) on every render.
  const EmptyOverlay = useMemo(
    () =>
      function EmptyOverlay() {
        return <OpportunitiesEmpty article={article} lower={lower} plural={plural} />;
      },
    [article, lower, plural]
  );
  const Toolbar = useMemo(
    () =>
      function Toolbar() {
        return <OpportunitiesToolbar plural={plural} />;
      },
    [plural]
  );

  return (
    <div className={classes.main}>
      <DataGridPremium<OpportunityRow>
        disableColumnFilter
        disableColumnMenu
        disableColumnReorder
        disableColumnResize
        disableMultipleRowSelection
        hideFooter
        showToolbar
        apiRef={apiRef}
        columnHeaderHeight={0}
        columns={colDefs}
        filterMode="server"
        loading={loading}
        rows={opportunities}
        slotProps={{
          loadingOverlay: {
            noRowsVariant: 'linear-progress',
            variant: 'linear-progress',
          },
        }}
        slots={{
          baseCheckbox: AlineCheckbox,
          baseTooltip: GridAlineTooltip,
          noRowsOverlay: EmptyOverlay,
          toolbar: Toolbar,
        }}
        sortingMode="server"
        sx={{
          [`& .${gridClasses.cell}`]: {
            '&:focus-within': {
              outline: 'none',
            },
            py: 1,
          },
          [`& .${gridClasses.columnHeader}`]: {
            '&:focus-within': {
              outline: 'none',
            },
          },
          border: 'none',
        }}
        onRowSelectionModelChange={model => {
          const selectedId = Array.from(model.ids).at(0);
          onSelectChange(selectedId ? String(selectedId) : null);
        }}
        onRowsScrollEnd={fetchMoreOpportunities}
      />
    </div>
  );
}

const useCellStyles = makeStyles()(theme => ({
  cell: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
  },
  cellText: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  cellType: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

export function OpportunityTitle({
  row,
  value,
}: GridRenderCellParams<OpportunityRow, Apollo.Maybe<string>>): JSX.Element {
  const { classes } = useCellStyles();
  const { selectedRowIds } = useGetSelectedRowIds();
  const isRowSelected = selectedRowIds.includes(String(row.id));

  return (
    <div className={classes.cell}>
      <Typography className={classes.cellText}>{value}</Typography>
      {Boolean(row.type) && (
        <Typography className={classes.cellType} color="text.secondary" variant="body2">
          {row.type}
        </Typography>
      )}
      <Spacer />
      {isRowSelected && (
        <>
          <Typography color="primary">Selected</Typography>
          <DoubleCheckIcon fill={IconColors.Primary} />
        </>
      )}
    </div>
  );
}

const useHeaderStyles = makeStyles()(_theme => ({
  header: {
    fontWeight: 600,
  },
}));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function OpportunityHeader(props: GridColumnHeaderParams<any, OpportunityRow>): JSX.Element {
  const { colDef } = props;
  const { classes } = useHeaderStyles();
  return <Typography className={classes.header}>{colDef.headerName}</Typography>;
}

const useEmptyStyles = makeStyles()(_theme => ({
  container: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
  },
}));

function OpportunitiesEmpty({
  article,
  lower,
  plural,
}: Pick<CrmRecordCopy, 'article' | 'lower' | 'plural'>): JSX.Element {
  const { classes } = useEmptyStyles();

  return (
    <div className={classes.container}>
      <Typography align="center" variant="subtitle1">
        {`No ${plural}. Add ${article} ${lower} or adjust your search.`}
      </Typography>
    </div>
  );
}

const useOpportunityToolbarStyles = makeStyles()(theme => ({
  search: {
    backgroundColor: theme.appColors.background,
    padding: theme.spacing(2),
  },
}));

function OpportunitiesToolbar({ plural }: { plural: string }): JSX.Element | null {
  const { classes } = useOpportunityToolbarStyles();
  const { search, setSearch } = useDataGridSearch();

  return (
    <div className={classes.search}>
      <Input
        fullWidth
        placeholder={`Search ${plural}...`}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        value={search}
        onChange={e => setSearch(e.target.value)}
      />
    </div>
  );
}
